import { Alert, Button } from "@mui/material";
import React, { useState } from "react";
import Modal from "../elements/Modal";
import QuestionForm from "./AiSurveyQuestion";
import useAuthToken from "../../hooks/getUserData";

export default function EditSurveyQuestion({
  question,
  fetchData,
  setMessage,
  setSnackbarOpen,
  categories,
}) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const token = useAuthToken();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setError(null);
  };

  const formatQuestionForApi = (values) => {
    const base = {
      category_id: values.category,
      title: values.title,
      description: values.description,
      type: values.questionType,
    };

    if (values.questionType === "single_choice") {
      base.options = values.options.map((opt, idx) => ({
        sno: idx + 1,
        title: opt.heading,
        description: opt.subtitle,
      }));
      const defaultOpt = values.options.find((opt) => opt.default);
      if (defaultOpt) {
        base.default_answer = defaultOpt.heading;
      }
    } else if (values.questionType === "choice_with_other") {
      base.options = values.options.map((opt, idx) => ({
        sno: idx + 1,
        title: opt.heading,
        value: opt.heading.replace(/[^a-zA-Z0-9]/g, "").toLowerCase(),
      }));
      base.input_label = values.textfieldConfig.label;
      base.input_placeholder = values.textfieldConfig.placeholder;
    } else if (
      ["single_line_textbox", "multi_line_textbox", "url_input"].includes(
        values.questionType
      )
    ) {
      base.input_label = values.textfieldConfig.label;
      base.input_placeholder = values.textfieldConfig.placeholder;
    }

    return base;
  };

  const formatOptionsForForm = (options, questionType, defaultAnswer) => {
    if (!options || !Array.isArray(options)) return [];
    if (questionType === "single_choice") {
      return options.map((opt) => ({
        heading: opt.title || "",
        subtitle: opt.description || "",
        default: opt.title === defaultAnswer,
      }));
    } else if (questionType === "choice_with_other") {
      return options.map((opt) => ({
        heading: opt.title || "",
        subtitle: opt.description || "",
      }));
    }
    return [];
  };

  const handleSubmit = async (values) => {
    setError(null);
    try {
      const formattedValues = formatQuestionForApi(values);
      const response = await fetch(
        `${process.env.REACT_APP_API_ENDPOINT}/v2/survey/question/${question.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify(formattedValues),
        }
      );

      if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(
          errorResponse.error?.message || "Failed to update question"
        );
      }

      setMessage("Question updated successfully");
      setSnackbarOpen(true);
      handleClose();
      await fetchData();
    } catch (error) {
      setError(`Failed to update question: ${error.message}`);
    }
  };

  const questionTypeValue = question.questionTypeRaw || "";

  return (
    <>
      <Button
        variant="text"
        sx={{ cursor: "pointer", textTransform: "none", fontSize: "inherit" }}
        onClick={handleClickOpen}
      >
        Edit Question
      </Button>

      <Modal
        open={open}
        width="sm"
        modalTitle="Edit Question"
        handleClose={handleClose}
      >
        {error && <Alert severity="error">{error}</Alert>}
        <QuestionForm
          onSubmit={handleSubmit}
          categories={categories}
          initialValues={{
            category: question.category_id || "",
            title: question.question || "",
            description: question.description || "",
            questionType: questionTypeValue,
            options: formatOptionsForForm(
              question.options,
              questionTypeValue,
              question.default_answer
            ),
            textfieldConfig: {
              label: question.input_label || "",
              placeholder: question.input_placeholder || "",
            },
          }}
        />
      </Modal>
    </>
  );
}